/* ⚠️ GENERATED from app/services/sessionPolicy.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/sessionPolicy.ts — how long a linked browser stays linked.
//
// 🟥 A TIMER IN THE TAB IS NOT A SECURITY CONTROL. It is JavaScript in a browser
//    the attacker is sitting in front of: closing the tab kills it, suspending
//    the laptop stops it firing, devtools pauses it, and none of that is needed
//    anyway because IndexedDB can simply be read. Anything enforced only in the
//    page is a suggestion. So this file draws a hard line between two jobs:
//
//      • EXPIRY  — decided from timestamps, enforced in Firestore rules and at
//                  socket auth against SERVER time. The client cannot lie to it.
//      • LOCKING — hides the screen and drops plaintext. Hygiene, best-effort,
//                  and honestly labelled as such.
//
// 🟥 AND ELAPSED TIME IS SUBTRACTED, NEVER ACCUMULATED. A setInterval that counts
//    up does not survive sleep, tab discard, or background throttling — which is
//    exactly how these features fail OPEN in the field: the laptop is shut for
//    two days, the timer never ticks, and the session that should have gone is
//    still there when it opens. Every question here is answered by subtracting
//    two timestamps.

/* ── the numbers ──────────────────────────────────────────────────────────
   🟢 A MESSENGER, NOT A BANK. The 15-minute figure comes from PCI-DSS 8.2.8,
      whose scope is cardholder data environments; NIST SP 800-63B allows 30 days
      of inactivity at AAL1, which is what a consumer chat app is. WhatsApp Web,
      Signal Desktop and Telegram Web ship no idle timeout at all. A chat client
      that logs you out mid-conversation is not more secure, it is less used.   */
export const IDLE_EXPIRY_MS = 14 * 24 * 60 * 60 * 1000; // trusted machine, no activity
export const ABSOLUTE_MAX_MS = 30 * 24 * 60 * 60 * 1000; // bounds a stolen refresh token

/* 🟥 THE SHARED MACHINE IS THE CASE A GLOBAL TIMER IS ACTUALLY REACHING FOR, and
   it is far better served by asking once at pairing than by punishing everyone
   on their own laptop for the library computer's sake. */
export const SHARED_IDLE_LOCK_MS = 15 * 60 * 1000; // library computer: lock the screen
export const SHARED_IDLE_EXPIRY_MS = 30 * 60 * 1000; // …then stop being a session at all
export const SHARED_MAX_MS = 12 * 60 * 60 * 1000; // …and it is gone the same day regardless

/* 🟥 THE LOCK AND THE EXPIRY MUST NOT BE THE SAME NUMBER. Set equal, the expiry
   is reached on the same tick as the lock and — because a session that is gone
   outranks one that is merely locked — the lock is never once returned. The
   screen would go straight from live to signed-out and the unlock prompt this
   whole layer exists to show would be dead code. Locking has to come FIRST and
   have room to mean something. */

/* 🟢 A HEARTBEAT IS A WRITE. Every keystroke touching Firestore would cost more
   than the feature is worth, so activity is coalesced: at most one write per
   window, and only when something actually happened. */
export const HEARTBEAT_MS = 5 * 60 * 1000;

/* ── how long the PLAINTEXT lives, which is a different question ───────────

   🟥 EXPIRY AND FORGETTING WERE THE SAME NUMBER, AND THEY ANSWER DIFFERENT
      QUESTIONS. "How often do I re-approve this browser from my phone?" is about
      annoyance, and fourteen days is a normal answer to it. "How long does my
      decrypted archive sit on that disk?" is about a laptop somebody left at
      home, and fourteen days is a terrible answer to that. Tying them together
      meant the only way to shorten the second was to shorten the first — which
      buys nothing, because IndexedDB is readable whether the session is alive or
      not. The session being expired was never the protection; the WIPE it
      triggers was, and the wipe can happen on its own schedule.

   🟢 SO THE PLAINTEXT GOES EARLY AND THE SESSION STAYS. No prompt, no lock, no
      re-pairing: the archive is dropped, the identity key and the session are
      kept, and coming back re-syncs from the phone exactly as it did on the day
      this browser was linked. What it costs is a wait; what it buys is that a
      machine nobody is using holds nothing worth taking.                       */
export const PLAINTEXT_IDLE_MS = 24 * 60 * 60 * 1000;
/* a machine that is not ours forgets on the same clock as its lock */
export const SHARED_PLAINTEXT_IDLE_MS = 15 * 60 * 1000;

/* 🟢 AND AN ENROLLED BROWSER DOES NOT NEED TO FORGET AT ALL. Wiping exists
   because plaintext on a disk cannot be protected any other way; ciphertext can,
   so a browser with a vault LOCKS instead — and can do it far sooner, because
   locking costs nothing to undo. Half an hour of absence makes the caches
   unreadable; the data is still there when its owner comes back.

   🟥 THE TWO ARE ALTERNATIVES, NOT A LADDER. Running the 24-hour wipe on an
      enrolled browser as well would throw away data that was already safe, and
      charge the user a re-sync for a protection they had paid for properly. */
export const VAULT_LOCK_IDLE_MS = 30 * 60 * 1000;

/**
 * Should the decrypted caches be dropped?
 *
 * 🟥 I WROTE THIS TO FAIL CLOSED AND IT WAS WRONG. The reasoning was "being wrong
 *    here only costs a re-sync — when the price of caution is a progress bar,
 *    caution is free", and that sentence is false in THIS app. Store-and-forward
 *    deletes the message documents once a recipient has cached them, so
 *    `allo_web_history` is not a cache of something the server still holds: it is
 *    the browser's ONLY durable copy of the conversation. Dropping it does not
 *    cost a progress bar, it costs every message the person can see until their
 *    phone happens to be online to export them again.
 *
 * 🟥 AND "UNKNOWN" IS THE STATE EVERY BROWSER IS IN THE FIRST TIME THIS SHIPS.
 *    No stamp has been written yet, so the very first read wiped the history
 *    before anything could read it — messages appeared live and then vanished,
 *    which is exactly what was reported.
 *
 * 🟢 SO IT FAILS OPEN, LIKE EVERY OTHER ANSWER IN THIS FILE. An unknown start is
 *    not an old one. The caller stamps it and the window begins now; one day
 *    later, with a stamp that says so, the wipe happens for the reason it exists.
 */
export function plaintextExpired(lastActiveAt, now = Date.now(), shared) {
  const window = shared ? SHARED_PLAINTEXT_IDLE_MS : PLAINTEXT_IDLE_MS;
  const t = Number(lastActiveAt);
  /* 🟥 NOT `return true`. See above — this is the line that ate the history. */
  if (!Number.isFinite(t) || t <= 0) return false;
  /* 🟢 A future stamp is a wrong clock, and a wrong clock is not evidence of age
     either — treat it as "just now" rather than as a reason to destroy data. */
  if (t > Number(now)) return false;
  return Number(now) - t >= window;
}

/* 🟥 THE ROLLOUT MUST NOT LOG EVERYONE OUT. Every session doc that exists today
   predates this file and has no lastActiveAt. Reading "no timestamp" as "no
   activity" would expire the entire install base the moment the rules deploy —
   the feature working perfectly and being indistinguishable from an outage. An
   unstamped session is UNMIGRATED, not idle, and gets this long to say so. */
export const MIGRATION_GRACE_MS = 7 * 24 * 60 * 60 * 1000;
/** The caps that apply to this session — a shared machine gets the short ones. */
export function limitsFor(shared) {
  return shared ? {
    idle: SHARED_IDLE_EXPIRY_MS,
    max: SHARED_MAX_MS,
    lock: SHARED_IDLE_LOCK_MS
  } : {
    idle: IDLE_EXPIRY_MS,
    max: ABSOLUTE_MAX_MS,
    lock: null
  };
}

/**
 * What should happen to this session right now.
 *
 * 🟢 ORDER IS THE POLICY. A killed session outranks an expired one, which
 *    outranks a locked one: each answer is more final than the next, and a
 *    session that is gone must not be reported as merely locked — that would
 *    show an unlock prompt for something no unlock can bring back.
 */
export function sessionVerdict(f, now = Date.now()) {
  /* 🟥 THE DOC IS THE SESSION. Deleted from the phone's Active Sessions list, or
     never written — either way this browser has no standing, and "I could not
     read it" must not be read as "it is fine". */
  if (f && f.exists === false) return {
    action: 'expire',
    reason: 'no_session'
  };
  if (f && f.forceLogout === true) return {
    action: 'expire',
    reason: 'force_logout'
  };
  const shared = !!(f && f.shared);
  const {
    idle,
    max,
    lock
  } = limitsFor(shared);
  const created = num(f && f.createdAt);
  const active = num(f && f.lastActiveAt);
  if (created !== null && now - created >= max) {
    return {
      action: 'expire',
      reason: 'absolute_max'
    };
  }
  if (active === null) {
    /* 🟥 UNSTAMPED IS NOT IDLE — see MIGRATION_GRACE_MS. But it is not a licence
       either: an old session still has to start reporting within the grace, and
       a doc with no createdAt to measure the grace from has nothing to prove it
       is old rather than forged, so it is asked to re-establish itself. */
    if (created === null) return {
      action: 'expire',
      reason: 'no_timestamps'
    };
    if (now - created >= MIGRATION_GRACE_MS) return {
      action: 'expire',
      reason: 'never_reported'
    };
    return {
      action: 'ok',
      reason: 'awaiting_migration'
    };
  }
  if (now - active >= idle) return {
    action: 'expire',
    reason: 'idle_expired'
  };
  if (lock !== null && now - active >= lock) return {
    action: 'lock',
    reason: 'idle_lock'
  };
  return {
    action: 'ok',
    reason: 'active'
  };
}

/**
 * Is it worth writing lastActiveAt again?
 *
 * 🟥 NO ACTIVITY, NO HEARTBEAT — or the session never expires. A beat sent by a
 *    timer alone keeps a tab that nobody has touched for a fortnight alive for
 *    ever, which is the whole control defeated by the thing meant to feed it.
 */
export function shouldHeartbeat(lastWriteAt, now, sawActivity) {
  if (!sawActivity) return false;
  const last = num(lastWriteAt);
  if (last === null) return true; // never written — the first beat is due
  return now - last >= HEARTBEAT_MS;
}

/** ms until this session expires with no further activity; 0 once it has. */
export function expiresIn(f, now = Date.now()) {
  const {
    idle
  } = limitsFor(!!(f && f.shared));
  const active = num(f && f.lastActiveAt);
  if (active === null) return 0;
  return Math.max(0, idle - (now - active));
}
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}
